import clsx from 'clsx'
import React, { CSSProperties, FC, PropsWithChildren } from 'react'

import { useLocale } from '@Globals/Locale'

import styles from './Chip.module.scss'

export interface ChipProps extends PropsWithChildren {
  backgroundColor?: string
  className?: string
  color?: string | 'grey' | 'error' | 'red' | 'black' | 'yellow' | 'blue' | 'greyV2' | 'greyV3'
  icon?: {
    dimensions: {
      height: number
      width: number
    }
    url: string
  }
  size?: string | 'small' | 'medium'
  style?: CSSProperties
  textColor?: string
}

const svgOverrides: Record<string, { alt: string, className?: string, src: string }> = {
  blacksale: {
    alt: 'Black Sale',
    src: '/static/images/blacksale.svg',
  },
  clover: {
    alt: 'Clover',
    className: styles.maxHeight,
    src: '/static/images/clover.svg',
  },
  cybermonday: {
    alt: 'Cyber Monday',
    src: '/static/images/cybermonday.svg',
  },
  end_season_sale_large: {
    alt: 'End Season Sale',
    className: styles.endSeasonSaleLarge,
    src: '/static/images/end_season_sale_large.svg',
  },
  end_season_sale_large_fr: {
    alt: 'Promos de fin de saison',
    className: styles.endSeasonSaleLarge,
    src: '/static/images/end_season_sale_large_fr.svg',
  },
  end_season_sale_small: {
    alt: 'End Season Sale',
    src: '/static/images/end_season_sale_small.svg',
  },
  end_season_sale_small_fr: {
    alt: 'Promos de fin de saison',
    src: '/static/images/end_season_sale_small_fr.svg',
  },
  lateseasonsalesvg: {
    alt: 'Late Season Sale',
    src: '/static/images/lateseasonsale.svg',
  },
  magma: {
    alt: 'Magma',
    className: styles.maxHeight,
    src: '/static/images/magma.svg',
  },
  newclassics: {
    alt: 'New Classics',
    className: styles.maxHeight,
    src: '/static/images/newclassics.svg',
  },
  singlesday: {
    alt: 'Singles Day',
    src: '/static/images/singlesday.svg',
  },
  summer_sale_large: {
    alt: 'Summer Sale',
    className: styles.summerSaleLarge,
    src: '/static/images/summer_sale_large.svg',
  },
  summer_sale_large_fr: {
    alt: 'Soldes d\'été',
    className: styles.summerSaleLarge,
    src: '/static/images/summer_sale_large_fr.svg',
  },
  summer_sale_small: {
    alt: 'Summer Sale',
    src: '/static/images/summer_sale_small.svg',
  },
  summer_sale_small_fr: {
    alt: 'Soldes d\'été',
    src: '/static/images/summer_sale_small_fr.svg',
  },
  trendhimclubexclusive: {
    alt: 'Trendhim Club Exclusive',
    src: '/static/images/trendhimclubexclusive.svg',
  },
  valentine_a_large: {
    alt: '-14% Valentine Sale',
    className: styles.valentineLarge,
    src: '/static/images/valentine_a_large.svg',
  },
  valentine_a_small: {
    alt: '-14% Valentine',
    src: '/static/images/valentine_a_small.svg',
  },
  valentine_b_large: {
    alt: 'Valentine Sale',
    className: styles.valentineLarge,
    src: '/static/images/valentine_b_large.svg',
  },
  valentine_b_small: {
    alt: 'Valentine Sale',
    src: '/static/images/valentine_b_small.svg',
  },
}

export const Chip: FC<ChipProps> = ({
  backgroundColor,
  children,
  className,
  color,
  icon,
  size,
  style: styleProperty = {},
  textColor,
}) => {
  const locale = useLocale()

  const style = {
    '--chipTextColor': textColor,
    '--chipBackgroundColor': backgroundColor,
    ...styleProperty,
  } as CSSProperties

  const svgOverride = svgOverrides[`${color || ''}_${locale.split('-')[0]}`] || svgOverrides[color || '']
  if (svgOverride) {
    return (
      <img
        alt={svgOverride.alt}
        className={clsx(
          styles.svgChip,
          size && styles[size],
          svgOverride.className,
        )}
        src={svgOverride.src}
        style={style}
      />
    )
  }

  return (
    <div
      className={clsx(
        styles.chip,
        color === 'grey' && styles.grey,
        color === 'greyV2' && styles.greyV2,
        color === 'greyV3' && styles.greyV3,
        (color === 'red' || color === '20' || color === 'sale') && styles.error,
        color === 'error' && styles.error,
        color === 'black' && styles.black,
        color === 'event' && styles.event,
        color === 'blue' && styles.blue,
        color && styles[color],
        size && styles[size],
        className,
      )}
      style={style}
    >
      {icon?.url && <img alt="" className={styles.icon} src={icon.url} style={{ aspectRatio: `${icon.dimensions.width} / ${icon.dimensions.height}` }} />}
      {children}
    </div>
  )
}
